import { g } from "analytics";
import { DEMO_COLOUR_SWATCHES, FONT_FAMILY_OPTIONS, TEXT_COLOUR_SWATCHES } from "apparatus/text/appearance";
import { UpdateArrowProperties } from "arrows/command";
import { commandProcessor } from "command/command";
import { SENTINEL_INDETERMINATE_RADIO, SENTINEL_TEXT_PLACEHOLDER } from "panel/appearance_spec_converter";
import { sectionsForMultipleItems } from "panel/propertyPanel";
import { convertDemoSwatchesToRadioOptions } from "panel/shared";
import * as React from "react";
import { Articles } from "support/beacon";
import { ChemMark } from "text/parse";
import { createChemMarkGlobalConfigElements, PropertyPanelHint } from "text/ui";
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 25;
function createUpdateCommand(arrows, key, newValue) {
    return new UpdateArrowProperties(arrows.map(a => ({ id: a.id, value: { [key]: newValue }, previousValue: { [key]: a[key] } })));
}
function getConsistentValue(arrows, key) {
    const value = arrows[0][key];
    if (arrows.length == 1)
        return value;
    for (const arrow of arrows) {
        if (arrow[key] !== value)
            return;
    }
    return value;
}
/** Creates Panel UI for arrows. */
export function arrowPanelUI(selected, arrows, app, tiers) {
    var _a, _b;
    // TODO: Fix this casting.
    const getComponents = () => arrows.map(a => app.getObjectComponent(a.id));
    const styleElements = [
        {
            spec: "slider",
            key: "arrow_font",
            label: "Text size",
            min: MIN_FONT_SIZE,
            max: MAX_FONT_SIZE,
            initialValue: arrows[0].fontSize,
            onStart: () => app.setIsInteracting(true),
            onChange: value => {
                getComponents().forEach(c => c === null || c === void 0 ? void 0 : c.setState({ fontSize: value }));
            },
            onEnd: (_, final) => {
                app.setIsInteracting(false);
                app.setArrowTemplate({ fontSize: final });
                commandProcessor.execute(createUpdateCommand(arrows, "fontSize", final));
            }
        },
        {
            spec: "select",
            key: "fontFace",
            label: "Font",
            value: arrows[0].fontFamily,
            options: FONT_FAMILY_OPTIONS,
            onSelectChange: (_, value) => {
                app.setArrowTemplate({ fontFamily: value });
                commandProcessor.execute(createUpdateCommand(arrows, "fontFamily", value));
            },
            id: "fontFace",
        },
        tiers.allow("boost") ?
            {
                spec: "color",
                key: "color",
                label: "Colour",
                value: arrows[0].textColor,
                swatches: TEXT_COLOUR_SWATCHES,
                onColorChange: value => {
                    getComponents().forEach(c => c.setState({ textColor: value }));
                },
                onColorEnd: finalValue => {
                    if (finalValue == arrows[0].textColor)
                        return;
                    commandProcessor.execute(createUpdateCommand(arrows, "textColor", finalValue));
                },
            } : {
            spec: "radio",
            id: "labelColor",
            key: "color-radio",
            label: "Colour",
            options: convertDemoSwatchesToRadioOptions(DEMO_COLOUR_SWATCHES),
            value: (_a = getConsistentValue(arrows, "textColor")) !== null && _a !== void 0 ? _a : SENTINEL_INDETERMINATE_RADIO,
            onChange: (previous, final) => {
                commandProcessor.execute(createUpdateCommand(arrows, "textColor", final));
            }
        },
        textAlignToggleButtons(arrows),
    ];
    const consistentShowArrow = getConsistentValue(arrows, "showArrow");
    const consistentEndStyle = getConsistentValue(arrows, "endStyle");
    var arrowSection = [];
    if (consistentShowArrow) {
        arrowSection.push({
            spec: "checkbox-group",
            label: "Options",
            key: "arrow-options",
            checkboxes: [
                {
                    label: "Show head",
                    value: consistentEndStyle !== undefined ? consistentEndStyle == "triangle" : undefined,
                    id: "show_arrow_head",
                    onCheckedChange: (value) => {
                        g("UpdateAppearance", `Arrow::endStyle`);
                        const endStyle = value ? "triangle" : "none";
                        commandProcessor.execute(createUpdateCommand(arrows, "endStyle", endStyle));
                        // Update the arrow so that next arrow drawn uses this style.
                        app.setArrowTemplate({ endStyle: endStyle });
                    },
                },
            ],
        }, {
            spec: "slider",
            label: "Thickness",
            key: "arrowThickness",
            min: 1.5, max: 6.0, step: 0.5,
            initialValue: arrows[0].arrowThickness,
            tier: "boost",
            onChange: value => {
                getComponents().forEach(c => c === null || c === void 0 ? void 0 : c.setState({ arrowThickness: value }));
            },
            onEnd: (_, final) => {
                commandProcessor.execute(createUpdateCommand(arrows, "arrowThickness", final));
                // Update template so next arrow uses this thickness.
                app.setArrowTemplate({ arrowThickness: final });
            }
        });
    }
    const currentLabel = getConsistentValue(arrows, "label");
    const isChemMarkEnabled = getConsistentValue(arrows, "isChemMarkEnabled");
    return {
        title: "Label",
        icon: "font",
        tabs: [
            {
                type: "text",
                sections: [
                    {
                        header: "Label",
                        icon: "font",
                        hideHeaderIfFirst: true,
                        elements: [
                            {
                                spec: "textarea",
                                rows: 3,
                                label: "Text",
                                key: "arrow_text",
                                text: currentLabel !== null && currentLabel !== void 0 ? currentLabel : "",
                                placeholder: SENTINEL_TEXT_PLACEHOLDER.if(currentLabel === undefined),
                                enableReplacements: true,
                                onFocus: () => { app.setIsEditingText(true); },
                                onChange: value => {
                                    getComponents().forEach(c => c === null || c === void 0 ? void 0 : c.setState({ label: value }));
                                },
                                onEnd: (previous, final) => {
                                    app.setIsEditingText(false);
                                    if (previous == final)
                                        return;
                                    g("UpdateAppearance", `Arrow::text`);
                                    commandProcessor.execute(createUpdateCommand(arrows, "label", final));
                                },
                            },
                        ],
                    },
                    {
                        header: "ChemText",
                        icon: ChemMark.faIconName,
                        helpArticleId: Articles.CHEMTEXT,
                        elements: [
                            {
                                spec: "checkbox-group",
                                key: "is_chem_mark_enabled",
                                label: ChemMark.uiDescription(),
                                wideLabel: true,
                                checkboxes: [{
                                        label: "",
                                        useSwitch: true,
                                        id: "is_chem_mark_enabled",
                                        value: isChemMarkEnabled,
                                        tier: "boost",
                                        allowWhenLocked: true,
                                        onCheckedChange: value => {
                                            g("UpdateAppearance", `Arrow::ChemMark`);
                                            commandProcessor.execute(createUpdateCommand(arrows, "isChemMarkEnabled", value));
                                        }
                                    }]
                            },
                            React.createElement(PropertyPanelHint, { key: "chemmark_hint", showUpgradeHint: (_b = getConsistentValue(arrows, "isChemMarkEnabled")) !== null && _b !== void 0 ? _b : false }),
                            ...createChemMarkGlobalConfigElements(isChemMarkEnabled !== null && isChemMarkEnabled !== void 0 ? isChemMarkEnabled : false)
                        ]
                    }
                ]
            },
            {
                type: "text-style",
                colorDot: getConsistentValue(arrows, "textColor"),
                sections: [
                    {
                        header: "Style",
                        icon: "font",
                        elements: styleElements
                    },
                    {
                        header: "Arrow",
                        icon: "long-arrow-alt-right",
                        toggle: {
                            value: consistentShowArrow, id: "show-arrow",
                            onCheckedChange: value => {
                                g("UpdateAppearance", `Arrow::showArrow`);
                                commandProcessor.execute(createUpdateCommand(arrows, "showArrow", value));
                            }
                        },
                        elements: arrowSection,
                    },
                ]
            },
            ...(arrows.length > 1 ? sectionsForMultipleItems(selected, app).tabs : [])
        ]
    };
}
const TEXT_ALIGN_OPTIONS = ["left", "center", "right"];
function textAlignToggleButtons(arrows) {
    const consistentValue = getConsistentValue(arrows, "textAlign");
    const currentIndex = consistentValue ? TEXT_ALIGN_OPTIONS.indexOf(consistentValue) : -1;
    return {
        spec: "toggle-horizontal-buttons",
        highlightIndex: currentIndex,
        key: "textAlign",
        label: "Text Align",
        size: "icon",
        tier: "boost",
        buttons: TEXT_ALIGN_OPTIONS.map(align => {
            return {
                buttonLabel: align.capitalise(),
                icon: "align-" + align,
                onClick: () => {
                    commandProcessor.execute(createUpdateCommand(arrows, "textAlign", align));
                }
            };
        })
    };
}
